import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Images, Star, Wrench, Inbox, BarChart3, Globe, ArrowRight } from "lucide-react";
import { adminAxios } from "../../lib/adminApi";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [g, r, s, q] = await Promise.all([
          adminAxios.get("/admin/gallery"),
          adminAxios.get("/admin/reviews"),
          adminAxios.get("/admin/services"),
          adminAxios.get("/admin/quotes"),
        ]);
        setStats({
          gallery: g.data.images.length,
          reviews: r.data.reviews.length,
          services: s.data.services.length,
          quotes: q.data.quotes.length,
          latest: q.data.quotes.slice(0, 5),
        });
      } catch {}
    })();
  }, []);

  const cards = [
    { to: "/admin/gallery", label: "Gallery", icon: Images, count: stats?.gallery, hint: "Upload & manage installation photos" },
    { to: "/admin/reviews", label: "Reviews", icon: Star, count: stats?.reviews, hint: "Add, edit Google-style reviews" },
    { to: "/admin/services", label: "Services", icon: Wrench, count: stats?.services, hint: "Edit the 4 service pillars" },
    { to: "/admin/quotes", label: "Leads", icon: Inbox, count: stats?.quotes, hint: "View quote submissions" },
  ];

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Welcome back, Murtaza</h1>
        <p className="text-slate-500 mt-1">Here's a quick look at what's live on your website.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Link
              key={c.to}
              to={c.to}
              className="group bg-white rounded-2xl border border-slate-100 p-5 card-soft hover:-translate-y-0.5 transition-all"
            >
              <div className="w-10 h-10 rounded-xl bg-[#E8F4F8] text-[#1C6B8A] flex items-center justify-center mb-3 group-hover:bg-[#1C6B8A] group-hover:text-white transition-colors">
                <Icon className="w-5 h-5" />
              </div>
              <div className="font-display text-3xl font-bold text-slate-900">
                {c.count ?? "—"}
              </div>
              <div className="text-sm font-semibold text-slate-800">{c.label}</div>
              <div className="text-xs text-slate-500 mt-1 leading-snug">{c.hint}</div>
            </Link>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-slate-100 p-6 card-soft">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold">Recent Leads</h2>
            <Link to="/admin/quotes" className="text-xs font-semibold text-[#1C6B8A] flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {stats?.latest?.length ? (
            <div className="space-y-3">
              {stats.latest.map((q) => (
                <div key={q.id} className="flex items-start justify-between gap-3 text-sm border-b border-slate-100 pb-3 last:border-none last:pb-0">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{q.name}</div>
                    <div className="text-xs text-slate-500 truncate">{q.service} · {q.phone}</div>
                  </div>
                  <div className="text-[11px] text-slate-400 shrink-0">
                    {new Date(q.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-500">No leads yet.</div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 card-soft">
          <h2 className="font-display text-lg font-semibold mb-4">Quick actions</h2>
          <div className="space-y-2">
            <Link to="/admin/stats" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50">
              <BarChart3 className="w-4 h-4 text-[#1C6B8A]" />
              <span className="text-sm font-medium">Update trust stats (Years / Clients / Projects)</span>
            </Link>
            <Link to="/admin/content" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50">
              <Globe className="w-4 h-4 text-[#1C6B8A]" />
              <span className="text-sm font-medium">Edit site content (address, phones, socials)</span>
            </Link>
            <Link to="/admin/gallery" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50">
              <Images className="w-4 h-4 text-[#1C6B8A]" />
              <span className="text-sm font-medium">Upload a new installation photo</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
